import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { policyDisplayName } from "@/lib/policyDisplay";

/**
 * Floating dropdown that lets users pick a Policy to mention in a contentEditable.
 * The parent component decides when to open it (on `@` keystroke) and passes the
 * current query + anchor position. On pick, we insert a chip at the caret and
 * call onInserted().
 *
 * Chip HTML (matches CSS in index.css):
 *   <a class="policy-mention" data-policy-id="ID" href="/policy/ID"
 *      target="_blank" rel="noopener" contenteditable="false">TYPE title Rn</a>
 */
export default function MentionPopover({ query, position, editorRef, onClose, onInserted }) {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/policies/mention", { params: { q: query || "" } });
                setItems(data); setActive(0);
            } finally { setLoading(false); }
        }, 150);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const onKey = (e) => {
            if (!items.length && e.key !== "Escape") return;
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); }
            else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[active]); }
            else if (e.key === "Escape") { e.preventDefault(); onClose(); }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [items, active, onClose]);

    const pick = (p) => {
        if (!p) return;
        insertMentionChip(editorRef.current, p, query);
        onInserted(p);
    };

    if (!position) return null;
    return (
        <div ref={boxRef} data-testid="mention-popover"
            style={{ position: "fixed", left: position.x, top: position.y, zIndex: 60 }}
            className="w-80 bg-white border border-neutral-200 rounded-md shadow-lg max-h-[50vh] overflow-y-auto">
            <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-neutral-500 bg-neutral-50 border-b border-neutral-100">
                Policy · @{query || ""}
            </div>
            {loading && <div className="p-3 text-xs text-neutral-500">Ricerca…</div>}
            {!loading && items.length === 0 && (
                <div className="p-3 text-xs text-neutral-500" data-testid="mention-empty">Nessuna policy pubblicata</div>
            )}
            <ul>
                {items.map((p, idx) => (
                    <li key={p.id}>
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                            data-testid={`mention-item-${p.id}`}
                            className={`w-full text-left px-3 py-2 text-sm ${idx === active ? "bg-neutral-100" : "hover:bg-neutral-50"}`}>
                            <div className="text-neutral-900 truncate">{policyDisplayName(p)}</div>
                            {p.series && <div className="text-[11px] text-neutral-500 truncate">Serie: {p.series}</div>}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Utility: replaces the `@query` before the caret with a chip node. */
function insertMentionChip(editor, policy, query) {
    if (!editor) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // Walk back from the caret in the current text node to find the "@" start
    const node = range.startContainer;
    if (node.nodeType !== 3) return; // only text nodes
    const caret = range.startOffset;
    const text = node.textContent || "";
    const atIdx = text.lastIndexOf("@", caret - 1);
    if (atIdx < 0) return;
    // Delete "@query"
    const delRange = document.createRange();
    delRange.setStart(node, atIdx);
    delRange.setEnd(node, caret);
    delRange.deleteContents();
    // Insert the chip element
    const chip = document.createElement("a");
    chip.className = "policy-mention";
    chip.setAttribute("data-policy-id", policy.id);
    chip.setAttribute("href", `/policy/${policy.id}`);
    chip.setAttribute("target", "_blank");
    chip.setAttribute("rel", "noopener");
    chip.setAttribute("contenteditable", "false");
    chip.textContent = policyDisplayName(policy);
    delRange.insertNode(chip);
    // Insert a trailing space and place the caret after it
    const space = document.createTextNode("\u00A0");
    chip.parentNode.insertBefore(space, chip.nextSibling);
    const after = document.createRange();
    after.setStartAfter(space); after.collapse(true);
    sel.removeAllRanges(); sel.addRange(after);
    // Bubble input event so parents auto-save
    editor.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * Helper hook: given an editorRef (contentEditable), returns mention state
 * ({ query, position }) that parents can pass to <MentionPopover />.
 * The parent must attach the returned `handlers` to the editor.
 */
export function useMention(editorRef) {
    const [state, setState] = useState(null); // { query, position }

    const check = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) { setState(null); return; }
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        if (!editorRef.current || !editorRef.current.contains(node)) { setState(null); return; }
        if (node.nodeType !== 3) { setState(null); return; }
        const caret = range.startOffset;
        const text = node.textContent || "";
        const atIdx = text.lastIndexOf("@", caret - 1);
        if (atIdx < 0) { setState(null); return; }
        // Ensure the `@` is at word start (space or start-of-node before it)
        if (atIdx > 0 && !/\s/.test(text[atIdx - 1])) { setState(null); return; }
        const q = text.slice(atIdx + 1, caret);
        if (/[\s\n]/.test(q)) { setState(null); return; } // whitespace terminates
        if (q.length > 40) { setState(null); return; }
        const r = range.getBoundingClientRect();
        setState({ query: q, position: { x: r.left, y: r.bottom + 4 } });
    };

    const close = () => setState(null);

    return {
        state, close,
        handlers: {
            onKeyUp: check,
            onMouseUp: check,
            onInput: () => setTimeout(check, 0),
        },
    };
}
